import axios from 'axios';
import { nanoid } from 'nanoid';
import * as Sentry from '@sentry/react';

import appSettings from '../app-settings';

const API_URL = `${appSettings.baseUrl}`;

export const getAudienceSizeCount = async ({
  tagIds,
  segmentIds,
  excludeTagIds,
  excludeSegmentIds,
  campaignLabelIds,
}) => {
  const params = { unsubscribed: false };
  if (segmentIds?.length) params.segment_ids = segmentIds.join(',');
  if (tagIds?.length) params.list_ids = tagIds.join(',');
  if (excludeSegmentIds?.length) params.exclude_segment_ids = excludeSegmentIds.join(',');
  if (excludeTagIds?.length) params.exclude_list_ids = excludeTagIds.join(',');
  if (campaignLabelIds?.length) params.campaign_label_ids = campaignLabelIds.join(',');
  const res = await axios.get(`${API_URL}/contacts/count`, { params });
  return res.data.count;
};

export const getTemplate = async (templateId, templateVersion, suggested = false) => {
  let requestUrl = `${API_URL}/email-templates/${templateId}/${templateVersion}`;
  if (suggested) requestUrl += '?suggested=true';
  const templateResponse = await axios.get(requestUrl);
  const { downloadUrl, ...rest } = templateResponse.data;
  const htmlResponse = await axios.get(downloadUrl, {
    headers: { Authorization: '' },
  });
  return { ...rest, html: htmlResponse.data };
};

export const createTemplate = async (design, { order, type }, emailConfig) => {
  const response = await axios.post(`${API_URL}/email-templates`, {
    design,
    order,
    type,
    emailConfig,
  });
  return response.data;
};

export const updateTemplate = async (templateId, version, design, emailConfig) => {
  const res = await axios.put(`${API_URL}/email-templates/${templateId}/${version}`, {
    design: JSON.stringify(design),
    emailConfig,
  });
  return res.data;
};

export const uploadHtml = async (uploadUrl, html) => {
  await axios.put(uploadUrl, html, { headers: { 'Content-Type': 'text/html', Authorization: '' } });
};

export const getDefaultSectionTemplates = async (sections) => {
  if (!sections) return {};
  const sectionTemplates = await Promise.all(
    Object.keys(sections).map(async (sectionKey) => {
      const templateId = sections[sectionKey]?.templateId;
      if (!templateId) return { sectionKey, template: null };
      const template = await getTemplate(templateId, '1');
      return { sectionKey, template };
    }),
  );
  return sectionTemplates.reduce((acc, { sectionKey, template }) => {
    acc[sectionKey] = template;
    return acc;
  }, {});
};

export const acceptSuggestedCampaign = async (campaignId, templateId) => {
  const response = await axios.patch(`${API_URL}/campaigns/accept-suggested`, { campaignId, templateId });
  return response.data;
};

export const restoreSuggestedCampaign = async (campaignId) => {
  const response = await axios.patch(`${API_URL}/campaigns/restore-suggested/${campaignId}`);
  return response.data;
};

export const getAllCampaigns = async () => {
  const response = await axios.get(`${API_URL}/campaigns`);
  return response.data;
};

export const getSuggestedCampaigns = async () => {
  const response = await axios.get(`${API_URL}/campaigns/suggested`);
  return response.data;
};

export const sendTestEmail = async (data) => {
  await axios.post(`${API_URL}/campaigns/test-email`, data);
};

export const getLabels = async () => {
  const response = await axios.get(`${API_URL}/campaigns/labels`);
  return response.data;
};

/**
 * Campaign events only carry a campaign ID, so anything rendering an event has
 * to resolve the name itself. Cached for the session: the map is read once per
 * contact drawer open and campaign names change rarely.
 */
let campaignNameMapPromise = null;

export const getCampaignNameMap = async () => {
  if (!campaignNameMapPromise) {
    campaignNameMapPromise = axios
      .get(`${API_URL}/campaigns`)
      .then(({ data }) =>
        (data ?? []).reduce((map, campaign) => {
          if (campaign?.campaignId) map[campaign.campaignId] = campaign.name;
          return map;
        }, {}),
      )
      .catch((err) => {
        // A missing name is cosmetic — the timeline still renders every event —
        // so don't cache the failure and don't fail the caller.
        campaignNameMapPromise = null;
        Sentry.captureException(err);
        return {};
      });
  }

  return campaignNameMapPromise;
};

export const resetCampaignNameMap = () => {
  campaignNameMapPromise = null;
};

export const createLabel = async (name) => {
  const response = await axios.post(`${API_URL}/campaigns/labels`, { name });
  return response.data;
};

export const getImageUploadUrl = async () => {
  const response = await axios.post(`${API_URL}/campaigns/images`);
  return response.data;
};

/**
 * Public URL of a file uploaded through `getImageUploadUrl`'s presigned POST.
 * The file is POSTed to the S3 `url`, but is linked from `publicBaseUrl`, the
 * campaign images CDN host.
 */
export const buildUploadedImageUrl = ({ fields, publicBaseUrl }, filename) =>
  `${publicBaseUrl}${fields.key.replace('${filename}', filename)}`;

export const uploadImage = async (uploadUrl, fields, file) => {
  try {
    // Generate a new random id to prevent file name collisions
    const newFile = new File([file], `${nanoid()}-${file.name}`, { type: file.type });
    await axios.postForm(
      uploadUrl,
      {
        ...fields,
        'Content-Type': file.type,
        file: newFile,
      },
      {
        headers: { Authorization: '' },
      },
    );

    return newFile;
  } catch (error) {
    throw { error, file };
  }
};

export const getLinkMetadata = async (url) => {
  const response = await axios.get(`${API_URL}/campaigns/link-preview`, { params: { url } });
  return response.data;
};

export const getCalendars = async () => {
  const response = await axios.get(`${API_URL}/campaigns/calendars`);
  return response.data;
};

export const updateCalendar = async (calendarId, data) => {
  const response = await axios.patch(`${API_URL}/campaigns/calendars/${calendarId}`, data);
  return response.data;
};

export const updateCampaign = async (campaignId, data) => {
  await axios.patch(`${API_URL}/campaigns/${campaignId}`, data);
};
